import { Trans } from "@lingui/react/macro";
import { useState } from "react";

import { Button } from "../../../components/ui-kit/button";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";

/**
 * Asks for the six-digit code an authenticator app shows. Brings its own form,
 * so callers must not wrap it in another one.
 *
 * The code starts empty on every mount, which is how a caller clears it:
 * remount for a fresh attempt, keep the element to let the user re-submit.
 */
export function OtpCodeInput(props: CodeInputProps) {
  return (
    <CodeForm
      {...props}
      normalize={normalizeOtpCode}
      isComplete={isCompleteOtpCode}
      width={100}
    />
  );
}

/** Asks for one of the single-use backup codes issued during enrollment. */
export function BackupCodeInput(props: CodeInputProps) {
  return (
    <CodeForm
      {...props}
      normalize={normalizeBackupCode}
      isComplete={isCompleteBackupCode}
      width={220}
    />
  );
}

interface CodeInputProps {
  submitLabel: string;
  onSubmit: (code: string) => void | Promise<unknown>;
}

/** What the two inputs share: one field, one button, submit when it's valid. */
function CodeForm({
  submitLabel,
  onSubmit,
  normalize,
  isComplete,
  width,
}: CodeFormProps) {
  const [code, setCode] = useState("");

  return (
    <Form
      className="flex items-center gap-2 mt-6"
      onSubmit={() => onSubmit(code)}
    >
      <label>
        <Trans>Code:</Trans>
      </label>
      <TextInput
        value={code}
        onChange={(newValue) => {
          setCode(normalize(newValue));
        }}
        width={width}
        autoFocus
      />
      <Button type="submit" disabled={!isComplete(code)}>
        {submitLabel}
      </Button>
    </Form>
  );
}

interface CodeFormProps extends CodeInputProps {
  normalize: (value: string) => string;
  isComplete: (code: string) => boolean;
  width: number;
}

const otpCodeLength = 6;

/** Codes are always digits, so strays are dropped as they are typed. */
function normalizeOtpCode(value: string) {
  return value.replace(/\D/g, "").slice(0, otpCodeLength);
}

function isCompleteOtpCode(code: string) {
  return code.length === otpCodeLength;
}

/**
 * Codes are issued as lowercase hex and the backend rejects anything else
 * outright — so whatever a password manager pads or capitalises comes off here.
 */
function normalizeBackupCode(value: string) {
  return value.trim().toLowerCase();
}

/**
 * Mirrors the backend's `BackupCode` validator — hex either side of a dash, 5
 * to 64 characters — so a mistyped code isn't spent as one of five attempts
 * and returned as an untranslated pydantic message.
 */
function isCompleteBackupCode(code: string) {
  return (
    code.length >= 5 && code.length <= 64 && /^[0-9a-f]+-[0-9a-f]+$/.test(code)
  );
}
